/**
 * ChatGPT Ads measurement for the app (mounted once in App).
 *
 *  - keeps oppref / UTM from signup links (only kept with permission, see adsAttribution)
 *  - asks for ads-measurement permission on public pages when the visitor has not decided
 *    on ocesha.ai or here (shared validated cookie, see adsConsent)
 *  - signed-in users: syncs permission + attribution to the account (server reporting via
 *    Stripe webhooks works without the browser) and fires the server's conversions with the
 *    pixel using the SAME event ids (OpenAI deduplicates)
 *  - the pixel only runs on public / conversion pages, never on courses or documents
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AdsConsent, clearPixelCookies, getAdsConsent, onAdsConsentChange, setAdsConsent } from "../utils/adsConsent";
import { applyConsentToAttribution, captureAttributionFromUrl, getAttributionForServer } from "../utils/adsAttribution";
import { ADS_TRACKING_ENABLED, isPixelPage, isPixelStarted, measure, startPixel, stopPixel } from "../utils/adsPixel";
import { isEmbedded } from "../utils/shoplineEmbedded";

const API = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");
const SYNCED_KEY = "ocs_ads_synced";             // consent value last synced in this tab

const isLoggedIn = () => {
  try { return localStorage.getItem("logged_in") === "true"; } catch { return false; }
};

type SyncResult = { consent: AdsConsent; user: Record<string, string>; events: any[] };

const syncWithServer = async (consent: AdsConsent, source: string, wantEvents: boolean): Promise<SyncResult | null> => {
  try {
    const res = await fetch(`${API}/api/ads/sync`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        consent: consent === "unknown" ? undefined : consent,
        consent_source: source,
        attribution: getAttributionForServer(),
        want_events: wantEvents,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
};

const fetchEvents = async (): Promise<any[]> => {
  try {
    const res = await fetch(`${API}/api/ads/events`, { credentials: "include" });
    if (!res.ok) return [];
    return (await res.json())?.events || [];
  } catch { return []; }
};

const fire = (events: any[]) =>
  (events || []).forEach((e) => {
    if (e?.event_name && e?.event_id && e?.data?.type) measure(e.event_name, e.data, e.event_id);
  });

const AdsMeasurement: React.FC = () => {
  const location = useLocation();
  const [consent, setConsent] = useState<AdsConsent>(() => getAdsConsent());
  const consentRef = useRef<AdsConsent>(consent);
  const userHashes = useRef<Record<string, string>>({});
  // switched off (REACT_APP_ADS_TRACKING_ENABLED not "Yes") behaves like SHOPLINE embedded: does nothing
  const embedded = !ADS_TRACKING_ENABLED || isEmbedded();
  const path = location.pathname;
  const pixelPage = isPixelPage(path);

  const runForPage = useCallback(async (c: AdsConsent, source: string, forceSync = false) => {
    if (embedded) return;
    const onPixelPage = isPixelPage(window.location.pathname);
    if (isLoggedIn()) {
      let synced: string | null = null;
      try { synced = sessionStorage.getItem(SYNCED_KEY); } catch { /* ignore */ }
      if (forceSync || onPixelPage || synced !== c) {
        const r = await syncWithServer(c, source, c === "granted" && onPixelPage);
        if (r) {
          try { sessionStorage.setItem(SYNCED_KEY, c); } catch { /* ignore */ }
          userHashes.current = r.user || {};
          if (c === "granted" && onPixelPage) {
            startPixel(userHashes.current);
            fire(r.events);
          }
          return;
        }
      }
    }
    if (c === "granted" && onPixelPage) startPixel(userHashes.current);
  }, [embedded]);

  // every page: capture attribution, start pixel / sync where allowed
  useEffect(() => {
    if (embedded) return;
    const c = getAdsConsent();
    if (c === "denied") clearPixelCookies();      // refused here or on ocesha.ai
    captureAttributionFromUrl();
    runForPage(c, "page");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, location.search]);

  // payment success page: the Stripe webhook may arrive a few seconds after the redirect
  useEffect(() => {
    if (embedded || path !== "/success" || consent !== "granted" || !isLoggedIn()) return;
    let n = 0;
    const timer = setInterval(async () => {
      n += 1;
      if (isPixelStarted()) fire(await fetchEvents());
      if (n >= 6) clearInterval(timer);
    }, 5000);
    return () => clearInterval(timer);
  }, [path, consent, embedded]);

  // permission changes (this app's prompt / settings, or the website via the shared cookie)
  useEffect(() => {
    if (!ADS_TRACKING_ENABLED) return;
    const apply = (c: AdsConsent, source: string) => {
      consentRef.current = c;
      setConsent(c);
      applyConsentToAttribution(c);
      if (c === "denied") stopPixel();
      runForPage(c, source, true);
    };
    const off = onAdsConsentChange(apply);
    const recheck = () => {
      const c = getAdsConsent();
      if (c !== consentRef.current) apply(c, "shared_cookie");   // changed on ocesha.ai
    };
    window.addEventListener("focus", recheck);
    return () => { off(); window.removeEventListener("focus", recheck); };
  }, [runForPage]);

  if (embedded || consent !== "unknown" || !pixelPage) return null;

  return (
    <div role="dialog" aria-label="Ad measurement" style={{
      position: "fixed", left: 16, right: 16, bottom: 16, zIndex: 2000, maxWidth: 560, margin: "0 auto",
      background: "#0f172a", color: "#fff", borderRadius: 12, padding: "14px 16px",
      boxShadow: "0 10px 30px rgba(0,0,0,.35)", fontSize: 14, lineHeight: 1.45,
    }}>
      <div style={{ marginBottom: 10 }}>
        May we measure whether our ads on ChatGPT led you here? With your permission we use the
        OpenAI ad measurement pixel on sign-up and checkout pages only, never on your courses or
        documents. You can change this anytime in Settings.{" "}
        <a href="/privacy" style={{ color: "#a5b4fc" }}>Privacy policy</a>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => setAdsConsent(false, "app_banner")} style={{
          background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.4)",
          borderRadius: 8, padding: "6px 14px", cursor: "pointer" }}>Decline</button>
        <button type="button" onClick={() => setAdsConsent(true, "app_banner")} style={{
          background: "#6366f1", color: "#fff", border: 0, borderRadius: 8, padding: "6px 14px",
          cursor: "pointer" }}>Allow</button>
      </div>
    </div>
  );
};

/** Settings control: lets the user grant or withdraw ads measurement at any time. */
export const AdsPrivacyChoices: React.FC = () => {
  const [consent, setConsent] = useState<AdsConsent>(() => getAdsConsent());
  useEffect(() => onAdsConsentChange((c) => setConsent(c)), []);
  if (!ADS_TRACKING_ENABLED) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span>
        Ad measurement (OpenAI ads pixel):{" "}
        <strong>{consent === "granted" ? "Allowed" : consent === "denied" ? "Not allowed" : "Not decided"}</strong>
      </span>
      {consent !== "granted" && (
        <button type="button" onClick={() => setAdsConsent(true, "app_settings")}>Allow</button>
      )}
      {consent !== "denied" && (
        <button type="button" onClick={() => setAdsConsent(false, "app_settings")}>
          {consent === "granted" ? "Withdraw permission" : "Decline"}
        </button>
      )}
    </div>
  );
};

export default AdsMeasurement;
